import { create } from 'zustand'

export type AgentPageContext = {
    /** Stable identifier for this context source (e.g. "keyword:abc123"). Used to dedupe and clear. */
    key: string
    /** Short label shown to the user (e.g. "Keyword: Best running shoes"). */
    label: string
    /** Entity type the agent should reason about (e.g. "keyword", "document", "dataset"). */
    entity?: string
    /** Mongo _id of the entity, when applicable. */
    id?: string
    /** External reference id (e.g. correlation id for keywords). */
    ref?: string
    /** Free-form structured payload the agent can read. Keep small. */
    metadata?: Record<string, unknown>
    /** When this context was registered (used to pick the freshest). */
    registeredAt: number
}

type PageContextState = {
    contexts: Record<string, AgentPageContext>
    setContext: (ctx: Omit<AgentPageContext, 'registeredAt'>) => void
    clearContext: (key: string) => void
    clearAll: () => void
}

export const useAgentPageContextStore = create<PageContextState>(set => ({
    contexts: {},
    setContext: ctx =>
        set(prev => ({
            contexts: {
                ...prev.contexts,
                [ctx.key]: { ...ctx, registeredAt: Date.now() },
            },
        })),
    clearContext: key =>
        set(prev => {
            if (!(key in prev.contexts)) return prev
            const next = { ...prev.contexts }
            delete next[key]
            return { contexts: next }
        }),
    clearAll: () => set(() => ({ contexts: {} })),
}))

/**
 * Returns the freshest context entry, or null if none registered.
 * Multiple sources can register at once (rare); we pick the most recent so
 * deeper child components win over shallower parents.
 */
export const getActivePageContext = (
    contexts: Record<string, AgentPageContext>
): AgentPageContext | null => {
    const entries = Object.values(contexts)
    if (entries.length === 0) return null
    return entries.reduce((latest, current) =>
        current.registeredAt > latest.registeredAt ? current : latest
    )
}

/**
 * Renders the context payload as a hidden block the agent can parse.
 * Returned string is empty when there is no context.
 */
export const formatPageContextForPrompt = (ctx: AgentPageContext | null): string => {
    if (!ctx) return ''

    const lines: string[] = ['<page_context>']
    lines.push(`The user is currently viewing: ${ctx.label}`)
    if (ctx.entity) lines.push(`entity: ${ctx.entity}`)
    if (ctx.id) lines.push(`id: ${ctx.id}`)
    if (ctx.ref) lines.push(`ref: ${ctx.ref}`)
    if (ctx.metadata && Object.keys(ctx.metadata).length > 0) {
        if (typeof ctx.metadata.templateLabel === 'string' && ctx.metadata.templateLabel.trim()) {
            lines.push(`template: ${ctx.metadata.templateLabel.trim()}`)
        }
        lines.push(`metadata: ${JSON.stringify(ctx.metadata)}`)
    }
    lines.push('When the user uses pronouns like "this", "it", or "the one I\'m looking at", they mean the entity above.')
    lines.push('</page_context>')
    return lines.join('\n')
}
